import './kp-volumes.less';
import type {Component} from 'typings/portaro.fe.types';
import type {Auth, Exemplar, RichDocument, Volume} from 'typings/portaro.be.types';
import type {IOnInit, IQService} from 'angular';
import ExemplarFilter from '../exemplar-filter';
import type ExemplarService from '../exemplar.service';
import type {VolumeDataService} from './volume.data-service';
import type ExemplarDataService from '../exemplar.data-service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import {getFirstByProperty} from 'shared/utils/array-utils.fp';
import type VolumeService from './volume.service';

interface IssueAwareVolume extends Volume {
    issues: Exemplar[];
}

export class KpVolumesComponentController implements KpVolumesComponentBindings, IOnInit {
    public document: RichDocument;
    public exemplarStatuses: number[];
    public properties: string[];
    public volumes: Volume[];
    public capabilities: KpVolumesComponentCapabilities;

    private exemplarFilter: ExemplarFilter;
    private volumesResolved: boolean;

    /*@ngInject*/
    constructor(
        private exemplarService: ExemplarService,
        private exemplarDataService: ExemplarDataService,
        private volumeDataService: VolumeDataService,
        private volumeService: VolumeService,
        private $q: IQService,
        private currentAuth: Auth) {
    }

    @ngAsync()
    public async $onInit(): Promise<void> {
        this.exemplarFilter = new ExemplarFilter(this.exemplarStatuses);
        this.volumesResolved = false;
        this.volumes = [];

        await this.$q.when(this.document);

        try {
            this.volumes = await this.volumeDataService.query({document: this.document.id});
        } finally {
            this.volumesResolved = true;
        }
    }

    public loadIssues(volume: IssueAwareVolume) {
        if (!volume.issues) {
            volume.issues = {$resolved: false} as Exemplar[];
            this.forceLoadIssues(volume);
        }
    }

    public forceLoadIssues(volume: IssueAwareVolume) {
        this.exemplarDataService.query({volume: volume.id, viewable: true, user: this.currentAuth.activeUser.id})
            .then((issues: Exemplar[]) => {
                volume.issues = issues;
                volume.issues = volume.issues.filter(this.exemplarFilter.forUser(this.currentAuth));
                volume.issues.$resolved = true;
            });
    }

    public haveVolumesAnyIssue(): boolean {
        return getFirstByProperty<Volume, keyof Volume>('withIssues', true)(this.volumes) !== null;
    }

    public createIssue(document: RichDocument, volume: Volume) {
        this.exemplarService.createIssue(document, volume);
    }

    public showVolumeCreation(document: RichDocument) {
        this.volumeService.createVolume(document);
    }

    public showVolumeEditation(volume: Volume) {
        this.volumeService.editVolume(volume);
    }
}

/**
 * @ngdoc component
 * @name kpVolumes
 * @module uc.record
 *
 * @param {Volume[]} volumes
 * @param {RichDocument} document
 * @param {string[]} properties
 * @param {number[]} exemplarStatuses
 * @param {KpVolumesComponentCapabilities} capabilities
 *
 * @description
 *
 */
export default class KpVolumesComponent implements Component<KpVolumesComponentBindings> {
    public static componentName = 'kpVolumes';

    public templateUrl = require('./kp-volumes.tpl.ftl');

    public bindings = {
        volumes: '<',
        document: '<',
        properties: '<',
        exemplarStatuses: '<',
        capabilities: '<'
    } as const;

    public controller = KpVolumesComponentController;
}

interface KpVolumesComponentBindings {
    volumes: Volume[];
    document: RichDocument;
    properties: string[];
    exemplarStatuses: number[];
    capabilities: KpVolumesComponentCapabilities;
}

export interface KpVolumesComponentCapabilities {
    canCreateAnyExemplar: boolean;
    canCreateAnyVolume: boolean;
    canEditAnyExemplar: boolean;
    canEditAnyVolume: boolean;
}